import { Component, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AuthPageLayoutComponent } from '../../../layout/auth-page-layout/auth-page-layout.component';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../../shared/services/auth.service';

@Component({
  selector: 'app-verify-email',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, AuthPageLayoutComponent],
  templateUrl: './verify-email.html',
})
export class VerifyEmail implements OnInit {

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private authService = inject(AuthService);
  private toastr = inject(ToastrService);
  private platformId = inject(PLATFORM_ID);

  status: 'loading' | 'success' | 'error' = 'loading';
  message = '';
  isLoading = true;

  resendEmail = '';
  isResending = false;
  resendSent = false;

  ngOnInit(): void {
    // Verifying consumes the one-time token, so only do it in the browser —
    // running it during the SSR render as well would spend the token there
    // and leave the browser's own request to fail.
    if (!isPlatformBrowser(this.platformId)) return;

    const token = this.route.snapshot.queryParamMap.get('token');

    if (!token) {
      this.status = 'error';
      this.message = 'Invalid verification link.';
      this.isLoading = false;
      return;
    }

    this.verifyEmail(token);
  }

  private verifyEmail(token: string): void {

    this.authService.verifyEmail(token)
      .subscribe({
        next: (res: any) => {
          this.isLoading = false;
          this.status = 'success';
          this.message = res?.data?.alreadyVerified
            ? 'Your email is already verified. You can log in.'
            : 'Email verified successfully.';
          this.toastr.success(this.message);

          setTimeout(() => {
            this.router.navigate(['/login']);
          }, 3000);
        },
        error: (err: any) => {
          this.isLoading = false;
          this.status = 'error';
          this.message =
            err?.error?.message ||
            'Verification link is invalid or expired.';
          this.toastr.error(this.message, 'Error');
        }
      });
  }

  resendVerification(): void {
    const email = this.resendEmail.trim();
    if (!email || this.isResending) return;

    this.isResending = true;
    this.authService.resendVerification(email).subscribe({
      next: () => {
        this.isResending = false;
        this.resendSent = true;
        this.toastr.success('If that account still needs verifying, a new link is on its way.');
      },
      error: (err: any) => {
        this.isResending = false;
        this.toastr.error(err?.error?.message ?? 'Could not resend the verification email. Please try again.', 'Error');
      },
    });
  }

}
